import React from 'react';
import { Container, Box, CircularProgress, Typography, Link } from '@mui/material';
import SEOHead from '../components/SEOHead';
import { useTermsContent } from '../hooks/useTermsContent';
import DocumentMarketingHero from '../components/DocumentMarketingHero';
import { DOCUMENT, documentPageCanvasSx, documentLegalPaperSx } from '../constants/documentTheme';
import { useTranslation } from 'react-i18next';

const PartnerTerms = () => {
  const { t } = useTranslation();
  const { content, loading, error } = useTermsContent('partner');

  return (
    <Box sx={documentPageCanvasSx}>
      <SEOHead
        title="Partner Agreement - Déjà Vu Nomad"
        description="Partner Agreement and Terms of Service for travel agencies, tour operators, and service providers on Déjà Vu Nomad platform."
        keywords="partner agreement, travel agency terms, tour operator agreement, commission structure, partner program"
        canonical="https://dejavunomad.com/partner-terms"
      />
      <DocumentMarketingHero
        eyebrow={t('home.footer.partners')}
        title="Partner Agreement"
        subtitle="Terms for travel agencies, tour operators, and service providers on Déjà Vu Nomad."
      />
      <Container maxWidth="xl" sx={{ py: { xs: 4, md: 6 } }}>
        <Box sx={documentLegalPaperSx}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
              <CircularProgress />
            </Box>
          ) : error ? (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <Typography color="error" paragraph>
                Failed to load partner agreement content.
              </Typography>
              <Link href="/terms-partner.html" target="_blank" rel="noopener">
                View Partner Agreement in new tab
              </Link>
            </Box>
          ) : (
            <Box 
              sx={{ 
                '& h2': { 
                  color: DOCUMENT.oxblood, 
                  fontFamily: DOCUMENT.display,
                  fontSize: '1.5rem',
                  fontWeight: 400,
                  mt: 4,
                  mb: 2,
                  pb: 1,
                  borderBottom: `1px solid ${DOCUMENT.rule}`
                },
                '& h3': {
                  fontSize: '1.1rem',
                  fontWeight: 600,
                  mt: 2,
                  mb: 1
                },
                '& p': {
                  mb: 2,
                  lineHeight: 1.7
                },
                '& ul': {
                  pl: 3,
                  mb: 2
                },
                '& li': {
                  mb: 1
                },
                '& table': {
                  width: '100%',
                  borderCollapse: 'collapse',
                  mb: 3
                },
                '& th': {
                  bgcolor: DOCUMENT.oxblood,
                  color: DOCUMENT.ivory,
                  p: 1.5,
                  textAlign: 'left'
                },
                '& td': {
                  p: 1.5,
                  borderBottom: '1px solid #ddd'
                },
                '& tr:nth-of-type(even)': {
                  bgcolor: '#f8f9fa'
                },
                '& .highlight-box': {
                  bgcolor: '#f0f7ff',
                  borderLeft: '4px solid #1a365d',
                  p: 3,
                  my: 3,
                  borderRadius: '0 8px 8px 0'
                },
                '& .warning-box': {
                  bgcolor: '#fff3cd',
                  borderLeft: '4px solid #ffc107',
                  p: 3,
                  my: 3,
                  borderRadius: '0 8px 8px 0'
                },
                '& .contact-box': {
                  bgcolor: '#e8f4f8',
                  p: 3,
                  borderRadius: 2,
                  mt: 3
                },
                '& .last-updated': {
                  color: '#666',
                  fontSize: '0.9rem',
                  mb: 3,
                  pb: 2,
                  borderBottom: '1px solid #eee'
                },
                '& a': {
                  color: DOCUMENT.oxblood
                }
              }}
              dangerouslySetInnerHTML={{ __html: content }}
            />
          )}
        </Box>
      </Container>
    </Box>
  );
};

export default PartnerTerms;
